/**
 * HotelContext — ambient hotel context for every hotel-scoped screen.
 *
 * Fetches /api/wf/v1/context and exposes:
 *   { organization_id, hotel_id, hotel_name, hotel_code,
 *     employee_id, role, role_template_id, permission_profile_id,
 *     scope, hotel_status, setup_completed, setup_progress_pct,
 *     is_impersonated }
 *
 * Also provides a `switchHotel(hotelId)` action that sets the
 * `active_hotel_id` cookie server-side and reloads the context.
 *
 * ADR-WF-1 v2.1 §25.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const HotelContext = createContext(null);

export function HotelContextProvider({ children }) {
  const [ctx, setCtx] = useState(undefined); // undefined = loading
  const [hotels, setHotels] = useState([]);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/wf/v1/context");
      setCtx(data);
    } catch (e) {
      setCtx(null);
      setError(e?.response?.data?.detail || "Failed to load hotel context");
    }
  }, []);

  const refreshHotels = useCallback(async () => {
    try {
      const { data } = await api.get("/wf/v1/hotels");
      setHotels(data || []);
    } catch {
      setHotels([]);
    }
  }, []);

  useEffect(() => {
    refresh();
    refreshHotels();
  }, [refresh, refreshHotels]);

  const switchHotel = useCallback(
    async (hotelId) => {
      await api.post("/wf/v1/switch", { hotel_id: hotelId });
      await refresh();
      // Reset any client-side query cache by reloading; simpler than
      // untangling every module's local state on P0.
      window.location.reload();
    },
    [refresh],
  );

  return (
    <HotelContext.Provider
      value={{ ctx, hotels, error, refresh, refreshHotels, switchHotel }}
    >
      {children}
    </HotelContext.Provider>
  );
}

export const useHotelContext = () => useContext(HotelContext);
